import React, { useCallback, useState } from 'react';
import styled from '@slicknode/stylemapper';
import type { EditorState } from 'lexical';
import { gql } from '@soundxyz/gql-string';
import { ERROR_TYPE, POSTING_ERROR_ACTIONS } from '@soundxyz/utils/constants/logging';
import { PostInputUI } from '@components/Feed/PostInputUI';
import { LexicalUtils } from '@components/Lexical/utils';
import { useAuth } from '@context/auth/base';
import { triggerLogin } from '@context/auth/store';
import { useToast } from '@context/toast';
import { RefetchOnComplete } from '@graphql/helpers/effects';
import {
  ActivityFeedHookDocument,
  PostCommentDocument,
  PostCommentsDocument,
  PostPagePostDocument,
  useMutation,
} from '@graphql/reactQuery';
import { useLogError } from '@hooks/useLogErrorNew';
import { DEFAULT_ERROR_TOAST_MESSAGE_TRY_AGAIN_LATER } from '@utils/constants';
import { getPostInputId } from '@utils/helpers';

/*#__PURE__*/ gql(/* GraphQL */ `
  mutation PostComment($input: CreatePostCommentInput!) {
    addPostComment(input: $input) {
      __typename

      ... on MutationAddPostCommentSuccess {
        data {
          id
          content
        }
      }

      ... on ValidationError {
        message
      }
    }
  }
`);

RefetchOnComplete({
  trigger: [PostCommentDocument],
  refetch: [PostCommentsDocument, PostPagePostDocument, ActivityFeedHookDocument],
});

export function PostCommentsInput({
  postId,
  type,
}: {
  postId: string;
  type: 'feed_replies_preview' | 'feed_post';
}) {
  const { mutateAsync, isLoading } = useMutation(PostCommentDocument, {});
  const { openToast } = useToast();
  const [lexicalState, setLexicalState] = useState<EditorState>();
  const logError = useLogError();

  const { isAuthenticatedWithAPI } = useAuth();

  const handleSubmit = useCallback(async () => {
    const text = LexicalUtils.generateTextForNode(LexicalUtils.getRootNode(lexicalState));
    if (!text.length) return;
    try {
      const response = await mutateAsync({
        input: {
          lexicalContent: lexicalState?.toJSON(),
          postId,
        },
      });
      if (response.data.addPostComment.__typename !== 'MutationAddPostCommentSuccess') {
        if (response.data.addPostComment.__typename === 'ValidationError') {
          logError({
            action: POSTING_ERROR_ACTIONS.REPLY_POST_COMMENT_FAILED,
            error: new Error(response.data.addPostComment.message),
            errorType: ERROR_TYPE.POSTING,
            isArtistOnlyPage: false,
            level: 'error',
            message: response.data.addPostComment.message,
            pillar: 'POSTING',
            toast: DEFAULT_ERROR_TOAST_MESSAGE_TRY_AGAIN_LATER,
            indexedTags: {
              postId,
            },
          });
        } else {
          logError({
            action: POSTING_ERROR_ACTIONS.REPLY_POST_COMMENT_FAILED,
            error: new Error("Couldn't add post comment"),
            errorType: ERROR_TYPE.POSTING,
            isArtistOnlyPage: false,
            level: 'error',
            message: "Couldn't add post comment",
            pillar: 'POSTING',
            toast: DEFAULT_ERROR_TOAST_MESSAGE_TRY_AGAIN_LATER,
            indexedTags: {
              postId,
            },
          });
        }
      } else {
        openToast({ text: 'Reply added', variant: 'success' });
      }

      // inject the response into the cache
    } catch (error) {
      logError({
        action: POSTING_ERROR_ACTIONS.REPLY_POST_COMMENT_FAILED,
        error,
        errorType: ERROR_TYPE.POSTING,
        isArtistOnlyPage: false,
        level: 'error',
        message: "Couldn't add post comment",
        pillar: 'POSTING',
        toast: DEFAULT_ERROR_TOAST_MESSAGE_TRY_AGAIN_LATER,
        indexedTags: {
          postId,
        },
      });
    }
  }, [lexicalState, mutateAsync, postId, logError, openToast]);

  return (
    <Container
      type={type}
      onClick={e => {
        if (!isAuthenticatedWithAPI) {
          triggerLogin();
          e.currentTarget.blur();
        }
      }}
    >
      <PostInputUI
        id={getPostInputId(postId)}
        as="input"
        type={type}
        isLocked={false}
        placeholder={isAuthenticatedWithAPI ? 'Add comment...' : 'Sign in to add a comment...'}
        disabled={isLoading || !isAuthenticatedWithAPI}
        onChange={setLexicalState}
        isLoading={isLoading}
        onSubmit={handleSubmit}
        onClick={() => {
          if (!isAuthenticatedWithAPI) {
            triggerLogin();
            return;
          }
        }}
      />
    </Container>
  );
}

const Container = styled('div', 'relative w-full', {
  variants: {
    type: {
      // Why 66px? Check services/webapp/components/Feed/Activities/FeedActivityItem.tsx
      feed_replies_preview: 'lg:pl-[66px]',
      feed_post: '',
    },
  },
});
